import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import {
  currentDatapointSelector,
  currentMultivalueDatapointSelector,
  getPasiveBboxesSelector,
} from '../../../../redux/modules/datapoints/selector';
import {
  isBoundedDatapoint,
  isTableDatapoint,
} from '../../../../redux/modules/datapoints/typedHelpers';
import { schemaMapSelector } from '../../../../redux/modules/schema/schemaMapSelector';
import { AnyDatapointDataST, PassiveBbox } from '../../../../types/datapoints';
import { rectangleFromCoordinates } from '../../document-canvas/utils/geometry';
import {
  getBoxCoordinates,
  getPointCoordinates,
} from '../../document-store/documentGeometry';
import { useCanvasGeometryActions } from '../../document-store/DocumentStore';
import { UseCanvasDimensions } from '../useCanvasDimensions';
import {
  getGridScrollPosition,
  GridBboxPosition,
} from './getGridScrollPosition';

const findDatapointWithPositionInCurrentTuple = (pbb: PassiveBbox[][]) => {
  const tupleBboxes = pbb.flat().filter(bbox => bbox && bbox.isInCurrentTuple);

  const firstBboxPage = tupleBboxes[0]?.page;

  // there is still a situation when we scroll to the first bbox in the tuple when user has only one bbox in the tuple
  return firstBboxPage && tupleBboxes.every(bbox => bbox.page === firstBboxPage)
    ? tupleBboxes[0]
    : undefined;
};

const activeDatapointWithPositionSelector = createSelector(
  currentMultivalueDatapointSelector,
  currentDatapointSelector,
  getPasiveBboxesSelector,
  schemaMapSelector,
  (multivalueDatapoint, datapoint, passiveBboxes, schemaMap) => {
    const getPositionToScroll = (datapointToScroll: AnyDatapointDataST) => {
      // Multivalue in sidebar
      if (
        multivalueDatapoint &&
        datapointToScroll.id === multivalueDatapoint.id
      ) {
        const { grid } = multivalueDatapoint;
        return grid ? getGridScrollPosition(grid, schemaMap) : undefined;
      }

      // Any bounded datapoint
      if (isBoundedDatapoint(datapointToScroll)) {
        return {
          page: datapointToScroll.content.page,
          position: datapointToScroll.content.position,
        };
      }

      // Unbounded datapoint in footer
      if (multivalueDatapoint) {
        return findDatapointWithPositionInCurrentTuple(passiveBboxes);
      }

      return undefined;
    };

    return datapoint
      ? {
          multivalueDatapoint,
          datapoint,
          bboxPosition: getPositionToScroll(datapoint),
          isGrid: multivalueDatapoint
            ? datapoint.id === multivalueDatapoint.id
            : false,
        }
      : undefined;
  }
);

export const useScrollToActiveDatapoint = ({
  dimensions,
}: {
  dimensions: UseCanvasDimensions;
}) => {
  const matrixActions = useCanvasGeometryActions();
  const previousDatapointId = useRef<number | undefined>(undefined);
  const previousMultivalueDatapointId = useRef<number | undefined>(undefined);

  const activeDatapointWithPosition = useSelector(
    activeDatapointWithPositionSelector
  );

  const scrollToBoundingBox = useCallback(
    (isGrid: boolean, content: GridBboxPosition) => {
      if (isGrid) {
        matrixActions.translateGridIntoViewport(
          getPointCoordinates(
            content.page,
            { x: content.position[0], y: content.position[1] },
            dimensions.pages
          )
        );
      } else {
        matrixActions.translateIntoViewport(
          getBoxCoordinates(
            content.page,
            rectangleFromCoordinates(content.position),
            dimensions.pages
          )
        );
      }
    },
    [dimensions.pages, matrixActions]
  );

  useEffect(() => {
    if (activeDatapointWithPosition) {
      const { bboxPosition, datapoint, isGrid } = activeDatapointWithPosition;

      const goingToDifferentDatapoint =
        datapoint.id !== previousDatapointId.current;

      const goingToParentMultivalue =
        isTableDatapoint(datapoint) &&
        datapoint.id === previousMultivalueDatapointId.current;

      if (goingToDifferentDatapoint && !goingToParentMultivalue) {
        if (bboxPosition) {
          // Looks like we need to wait for footer for two frames before scrolling
          // Mostly likely because of the makeResizable implementation
          // Viewport is resized few frames later.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // we need to wait another frame to get the correct viewport dimensions
              // eg. when going back from edit mode to validation screen
              // otherwise default dimensions are used and then we set wrong canvas state
              requestAnimationFrame(() => {
                scrollToBoundingBox(isGrid, bboxPosition);
              });
            });
          });
        }
      }
    }

    previousDatapointId.current = activeDatapointWithPosition?.datapoint.id;
    previousMultivalueDatapointId.current =
      activeDatapointWithPosition?.multivalueDatapoint?.id;
  }, [activeDatapointWithPosition, scrollToBoundingBox]);
};
